import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FONT_PROXIMA_13,
  FONT_PROXIMA_16,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AnalysisGraphNodeData} from '../analysis-graph/AnalysisGraphTypes';
import {ANALYSIS_FLOWCHART_NODE_WIDTH} from '../AnalysisFlowchartStyleConstants';
import {getVisualizationStepNodeContentsInfo} from './getVisualizationStepNodeContentsInfo';
import {GetStepNodeContentsFn, StepNodeContentsInfo} from './NodeContentsTypes';

const STEP_TYPE_TO_NODE_CONTENTS_FN: Partial<Record<PipelineStepType, GetStepNodeContentsFn>> = {
  [PipelineStepType.VISUALIZATION]: getVisualizationStepNodeContentsInfo,
};

function isSourceNode(step: GetAnalysisPipelineStep): boolean {
  return (
    step.inputStepDatas.length === 0 &&
    step.outputStepDatas.length === 1 &&
    step.outputStepDatas[0].dataFrames.length === 1
  );
}

function getStepNodeContents(step: GetAnalysisPipelineStep): StepNodeContentsInfo {
  if (STEP_TYPE_TO_NODE_CONTENTS_FN[step.definition.stepType] != null) {
    return STEP_TYPE_TO_NODE_CONTENTS_FN[step.definition.stepType]!(step);
  }
  if (isSourceNode(step)) {
    return {
      icon: 'table',
      title: step.outputStepDatas[0].dataFrames[0].name,
    };
  }
  if (step.definition.stepType === PipelineStepType.MAP_COLUMNS) {
    return {
      title: 'Map columns',
      subtitle: '(From replace table)',
    };
  }
  return {title: STEP_TYPE_TO_USER_FACING_NAME[step.definition.stepType]};
}

export default function StepNodeContents({
  step,
  isTemplate,
}: Pick<AnalysisGraphNodeData, 'step' | 'isTemplate'>): JSX.Element {
  const {title, icon: stepIcon, subtitle} = getStepNodeContents(step);

  const isFailed = step.statusCode === PipelineStepStatusCode.FAILED;
  const isRunning = !isFailed && step.statusCode !== PipelineStepStatusCode.SUCCEEDED;

  let icon = stepIcon;
  // Within a template, we don't care about the status of the steps, especially since output steps are added
  // as BLOCKED and we don't want to show the running icon
  if (!isTemplate) {
    icon = (isFailed ? 'exclamation-circle' : null) ?? (isRunning ? 'spinner-third' : null) ?? icon;
  }

  return (
    <div className={css(styles.nodeContentsWrapper)}>
      {icon && (
        <Tooltip
          text={step.statusMessage}
          isDisabled={step.statusMessage == null}
          placement={FloatingPlacement.LEFT}
        >
          <div className={classNames(css(styles.icon), isRunning && 'fa-spin')}>
            <BenchlingIcon icon={icon} />
          </div>
        </Tooltip>
      )}
      <div className={css(styles.textContainer)}>
        <div className={css(styles.title)}>{title}</div>
        {subtitle && <div className={css(styles.subtitle)}>{subtitle}</div>}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  nodeContentsWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
  },
  icon: {
    marginRight: 5,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    ...FONT_PROXIMA_16,
    ...multiLineEllipsisText(2),
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    maxWidth: ANALYSIS_FLOWCHART_NODE_WIDTH - 30,
  },
  subtitle: {
    ...FONT_PROXIMA_13,
    ...multiLineEllipsisText(2),
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    maxWidth: ANALYSIS_FLOWCHART_NODE_WIDTH - 30,
  },
});
